import type { ReactNode } from "react";
import { createBrowserRouter, Navigate, Outlet, type RouteObject, useLocation, useSearchParams } from "react-router";
import { AdminCabinetLayout, AdminSalonPage, AdminSalonsPage, RequireAdmin } from "../features/admin/index.js";
import { InvitePage, LoginPage } from "../features/auth/index.js";
import { BillingPage } from "../features/billing/index.js";
import { BookingPage, DataNoticePage } from "../features/booking-page/index.js";
import { BotSettingsPage, CabinetLayout, HomePage, MorePage, OwnerOnly, ProfilePage, TeamPage, UnknownQuestionsPage } from "../features/cabinet/index.js";
import { CalendarPage, CalendarRulesPage, MasterPage, MastersPage, ServicesPage } from "../features/calendar/index.js";
import { ClientCardPage, ClientsPage } from "../features/clients/index.js";
import { ConversationsPage } from "../features/conversations/index.js";
import { DemoChatPage } from "../features/demo-chat/index.js";
import { DataDeletionStatusPage, InstagramSettingsPage } from "../features/instagram/index.js";
import { MyBookingPage } from "../features/my-booking/index.js";
import { NotificationsPage, PushSettingsPage } from "../features/notifications/index.js";
import { BookingLinkPage, OnboardingPage, SalonProfilePage } from "../features/onboarding/index.js";
import { ReportsPage } from "../features/reports/index.js";
import { LandingPage, PricingPage, PrivacyPage, RefundPage, SiteLayout, TermsPage } from "../features/site/index.js";
import { Spinner } from "../ui/index.js";
import { CrashScreen } from "./CrashScreen.js";
import { Root } from "./Root.js";
import { useSession } from "./session.js";

function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (session.loading) return <Spinner />;
  if (!session.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return <>{children}</>;
}

/** `next`: только внутренний путь, чтобы не уводить на чужой сайт после входа. */
const safeNext = (value: string | null): string => (value?.startsWith("/") && !value.startsWith("//") ? value : "/");

function LoginRoute() {
  const [params] = useSearchParams();
  const session = useSession();
  const next = safeNext(params.get("next"));
  if (session.user) return <Navigate to={next} replace />;
  return <LoginPage next={next} />;
}

/** `/`: гость видит сайт (этап 7), вошедший кабинет, админ без салонов админку. */
function HomeRoute() {
  const session = useSession();
  if (session.loading) return <Spinner />;
  if (!session.user) return <LandingPage />;
  if (session.user.isAdmin && session.user.memberships.length === 0) return <Navigate to="/admin" replace />;
  return <HomePage />;
}

/** Страницы кабинета салона одни и те же для владельца (`/s/:salonId`) и для админа (`/admin/s/:salonId`). */
const cabinetPages: RouteObject[] = [
  { index: true, element: <Navigate to="calendar" replace /> },
  { path: "calendar", element: <CalendarPage /> },
  { path: "conversations", element: <ConversationsPage /> },
  // Открытый диалог: на телефоне экран переписки, на широком экране список слева и переписка справа.
  { path: "conversations/:conversationId", element: <ConversationsPage /> },
  { path: "clients", element: <ClientsPage /> },
  { path: "clients/:clientId", element: <ClientCardPage /> },
  { path: "notifications", element: <NotificationsPage /> },
  {
    path: "reports",
    element: (
      <OwnerOnly>
        <ReportsPage />
      </OwnerOnly>
    ),
  },
  { path: "demo", element: <DemoChatPage /> },
  { path: "settings", element: <MorePage /> },
  { path: "settings/profile", element: <ProfilePage /> },
  { path: "settings/notifications", element: <PushSettingsPage /> },
  {
    path: "settings/bot",
    element: (
      <OwnerOnly>
        <BotSettingsPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/unknown",
    element: (
      <OwnerOnly>
        <UnknownQuestionsPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/services",
    element: (
      <OwnerOnly>
        <ServicesPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/masters",
    element: (
      <OwnerOnly>
        <MastersPage />
      </OwnerOnly>
    ),
  },
  { path: "settings/masters/:masterId", element: <MasterPage /> },
  {
    path: "settings/calendar",
    element: (
      <OwnerOnly>
        <CalendarRulesPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/team",
    element: (
      <OwnerOnly>
        <TeamPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/instagram",
    element: (
      <OwnerOnly>
        <InstagramSettingsPage />
      </OwnerOnly>
    ),
  },
  { path: "billing", element: <BillingPage /> },
  {
    path: "onboarding",
    element: (
      <OwnerOnly>
        <OnboardingPage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/salon",
    element: (
      <OwnerOnly>
        <SalonProfilePage />
      </OwnerOnly>
    ),
  },
  {
    path: "settings/booking-link",
    element: (
      <OwnerOnly>
        <BookingLinkPage />
      </OwnerOnly>
    ),
  },
];

/**
 * Маршруты. Этапы добавляют свои: /book/:slug (6), /my/:token (6), /demo (3).
 * Этап 7: сайт (`/`, `/pricing`, `/terms`, `/refund`, `/privacy`), админка (`/admin`), подписка (`/s/:salonId/billing`).
 * Этап 4: статус заявки Meta на удаление данных (`/data-deletion/:code`, публично).
 */
export const router = createBrowserRouter([
  {
    // Корневой маршрут без пути: экран ошибки вместо стандартного «Unexpected Application Error!» и слежение за переходами.
    element: <Root />,
    errorElement: <CrashScreen />,
    children: [
      { path: "/login", element: <LoginRoute /> },
      { path: "/invite/:token", element: <InvitePage /> },
      { path: "/data-deletion/:code", element: <DataDeletionStatusPage /> },
      // Этап 6: публичные страницы салона, без входа.
      { path: "/book/:slug", element: <BookingPage /> },
      { path: "/my/:token", element: <MyBookingPage /> },
      { path: "/data-notice/:slug", element: <DataNoticePage /> },
      {
        path: "/",
        element: <Outlet />,
        children: [
          { index: true, element: <HomeRoute /> },
          {
            element: <SiteLayout />,
            children: [
              { path: "pricing", element: <PricingPage /> },
              { path: "terms", element: <TermsPage /> },
              { path: "refund", element: <RefundPage /> },
              { path: "privacy", element: <PrivacyPage /> },
            ],
          },
          {
            path: "s/:salonId",
            element: (
              <RequireAuth>
                <CabinetLayout />
              </RequireAuth>
            ),
            children: cabinetPages,
          },
          {
            path: "admin",
            element: (
              <RequireAdmin>
                <Outlet />
              </RequireAdmin>
            ),
            children: [
              { index: true, element: <AdminSalonsPage /> },
              { path: "salons/:salonId", element: <AdminSalonPage /> },
              { path: "s/:salonId", element: <AdminCabinetLayout />, children: cabinetPages },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
